import {LocationStrategy} from '@angular/common';
import {Attribute, Directive, ElementRef, HostBinding,
  HostListener, Input, OnChanges, OnDestroy, Renderer2, isDevMode } from '@angular/core';
import {Subscription} from 'rxjs/Subscription';

import { Router, ActivatedRoute, NavigationEnd, UrlTree, Params } from '@angular/router';
import { UtilityFunctions } from '../classes/UtilityFunctions';
import { DisplayStateService } from '../services/DisplayStateService';
import { ClientSettingsService } from '../services/ClientSettingsService';
import { AppStateService } from '../services/AppStateService';
import { keys } from 'lodash';
/**
 * port of angular routerLink: https://github.com/angular/angular/blob/5.2.9/packages/router/src/directives/router_link.ts
 * but with unique implementation for our platform routing mechanism.
 * 
 * You can set a platform context route as follows:
 *
 * ```
 * <a [platformRouterLink]="{'Source':'Examples','Category':'Charts','Module':'Example'}">
 *   link to user component
 * </a>
 * ```
 * RouterLink will use these to generate this link: `http://hostname/#/?c.Source=Examples&c.Category=Charts&c.Module=Example`.
 * 
 * You can simply provide the new route's context as the value (object) for the platformRouterLink directive
 * or you can specify the different routing parameter types individually as inputs (context, filters, sorts, display)
 * 
 * Alternatively you can optionally specify a string (one of 'context', 'filters', 'sorts', or 'display') as the value for commandsType 
 * to have it treat the values provided to the platformRouterLink attribute as a context, filters, sorts, or display type object
 *
 * Finally you can specify your own endcoded queryParams as an input to override all
 */
@Directive({selector: ':not(a)[platformRouterLink]'})
export class PlatformRouterLink {
  @Input() queryParams: {[k: string]: any}; // allow to manually specify all the query params
  @Input() commandsType = 'context'; // can specify if the object provided to the platformRouterLink is a collection of context values, filter values, sort values, or display values
  @Input() context: {[k: string]: any}; 
  @Input() filters: {[k: string]: any}; 
  @Input() sorts: {[k: string]: any}; 
  @Input() display: {[k: string]: any}; 
  @Input() fragment: string;
  @Input() preserveFragment: boolean;
  private commands: {[k: string]: any} = {};
  private preserve: boolean;

  constructor(
      private router: Router, private route: ActivatedRoute,
      private displayStateService: DisplayStateService,
      private clientSettingsService: ClientSettingsService,
      private appStateService: AppStateService,
      @Attribute('tabindex') tabIndex: string, renderer: Renderer2, el: ElementRef) {
    if (tabIndex == null) {
      renderer.setAttribute(el.nativeElement, 'tabindex', '0');
    }
  }

  @Input()
  set platformRouterLink(commands: {[k: string]: any}|string) {
    // look for an object (like query params), if none, try to parse the string, if none, warn..
    // by default these are assumed to be context params
    if (commands != null) {
      if (Array.isArray(commands)) {
        if (keys(commands).length) {
          this.commands = this.processCommands(commands);
        } else {
          this.sendWarn();
        }
      } else if (typeof commands === 'object') {
          this.commands = this.processCommands(commands);
      } else if (typeof commands === 'string') {
        try {
          commands = JSON.parse(commands);
        }catch(e) {}
        if (typeof commands === 'object') {
          this.commands = this.processCommands(commands);
        } else {
          this.sendWarn();
        }
      } else {
          this.sendWarn();
      }
    } else {
      this.commands = {};
    }
  }

  processCommands(commands: {[k: string]: any}|string) {
    let ckeys = keys(commands);
    
    let existingParams = keys(this.displayStateService.ExistingParams());
    let existingKeys = keys(existingParams);

    let newParams: Params = [];

    if (this.commandsType === 'context') {      
      existingKeys.filter(ek => !ek.startsWith('c.')).forEach(ek => newParams[ek] = existingParams[ek] );
      ckeys.forEach(k => newParams['c.' + k] = commands[k]);
    } else if (this.commandsType === 'filters') {
      existingKeys.filter(ek => !ek.startsWith('f.')).forEach(ek => newParams[ek] = existingParams[ek] );
      ckeys.forEach(k => newParams['f.' + k] = commands[k]);
    } else if (this.commandsType === 'sorts') {
      existingKeys.filter(ek => !ek.startsWith('s.')).forEach(ek => newParams[ek] = existingParams[ek] );      
      ckeys.forEach(k => newParams['s.' + k] = commands[k]);
    } else if (this.commandsType === 'display') {
      existingKeys.filter(ek => !ek.startsWith('d.')).forEach(ek => newParams[ek] = existingParams[ek] );
      ckeys.forEach(k => newParams['d.' + k] = commands[k]);
    }
    if (this.context !== undefined && this.context !== null) {
      keys(this.context).forEach(k => newParams['c.' + k] = this.context[k]);
    }
    if (this.filters !== undefined && this.filters !== null) {
      keys(this.filters).forEach(k => newParams['f.' + k] = this.filters[k]);
    }
    if (this.sorts !== undefined && this.sorts !== null) {
      keys(this.sorts).forEach(k => newParams['s.' + k] = this.sorts[k]);
    }
    if (this.display !== undefined && this.display !== null) {
      keys(this.display).forEach(k => newParams['d.' + k] = this.display[k]);
    }
    console.log('PlatformRouterLink: new keys -> ' + JSON.stringify(keys(newParams)));
    return newParams;
  }

  @Input()

  @HostListener('click')
  onClick(): boolean {
    this.displayStateService.Route(this.commands);
    return true;
  }

  get urlTree(): UrlTree {
    return this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: this.queryParams || this.commands,
      fragment: this.fragment,
      preserveFragment: attrBoolValue(this.preserveFragment),
    });
  }
  sendWarn() {
    UtilityFunctions.Log(
        1,
        'Invalid setting for [platformRouterLink]',
        this.clientSettingsService,
        this.appStateService);
  }
}

/**
 * @whatItDoes Lets you link to specific parts of your app.
 *
 * See {@link RouterLink} for more information.
 *
 * @ngModule RouterModule
 *
 * @stable
 */
@Directive({selector: 'a[platformRouterLink]'})
export class PlatformRouterLinkWithHref implements OnChanges, OnDestroy {
  @HostBinding('attr.target') @Input() target: string;
  @Input() queryParams: {[k: string]: any}; // allow to manually specify all the query params
  @Input() commandsType = 'context'; // can specify if the object provided to the platformRouterLink is a collection of context values, filter values, sort values, or display values
  @Input() context: {[k: string]: any}; 
  @Input() filters: {[k: string]: any}; 
  @Input() sorts: {[k: string]: any}; 
  @Input() display: {[k: string]: any}; 
  @Input() fragment: string;
  @Input() preserveFragment: boolean;
  private commands: {[k: string]: any} = {};
  private subscription: Subscription;
  private preserve: boolean;

  // the url displayed on the anchor element.
  @HostBinding() href: string;

  constructor(
      private router: Router, private route: ActivatedRoute,
      private displayStateService: DisplayStateService,
      private clientSettingsService: ClientSettingsService,
      private appStateService: AppStateService,
      private locationStrategy: LocationStrategy) {
    this.subscription = router.events.subscribe(s => {
      if (s instanceof NavigationEnd) {
        this.updateTargetUrlAndHref();
      }
    });
  }

  @Input()
  set platformRouterLink(commands: {[k: string]: any}|string) {
    // look for an object (like query params), if none, try to parse the string, if none, warn..
    // by default these are assumed to be context params
    if (commands != null) {
      if (Array.isArray(commands)) {
        if (keys(commands).length) {
          this.commands = this.processCommands(commands);
        } else {
          this.sendWarn();
        }
      } else if (typeof commands === 'object') {
          this.commands = this.processCommands(commands);
      } else if (typeof commands === 'string') {
        try {
          commands = JSON.parse(commands);
        }catch(e) {}
        if (typeof commands === 'object') {
          this.commands = this.processCommands(commands);
        } else {
          this.sendWarn();
        }
      } else {
          this.sendWarn();
      }
    } else {
      this.commands = {};
    }
  }

  processCommands(commands: {[k: string]: any}|string) {
    let ckeys = keys(commands);
    
    let existingParams = this.displayStateService.ExistingParams();
    let existingKeys = keys(existingParams);

    let newParams: Params = [];

    if (this.commandsType === 'context') {      
      existingKeys.filter(ek => !ek.startsWith('c.')).forEach(ek => newParams[ek] = existingParams[ek] );
      ckeys.forEach(k => newParams['c.' + k] = commands[k]);
    } else if (this.commandsType === 'filters') {
      existingKeys.filter(ek => !ek.startsWith('f.')).forEach(ek => newParams[ek] = existingParams[ek] );
      ckeys.forEach(k => newParams['f.' + k] = commands[k]);
    } else if (this.commandsType === 'sorts') {
      existingKeys.filter(ek => !ek.startsWith('s.')).forEach(ek => newParams[ek] = existingParams[ek] );      
      ckeys.forEach(k => newParams['s.' + k] = commands[k]);
    } else if (this.commandsType === 'display') {
      existingKeys.filter(ek => !ek.startsWith('d.')).forEach(ek => newParams[ek] = existingParams[ek] );
      ckeys.forEach(k => newParams['d.' + k] = commands[k]);
    }
    if (this.context !== undefined && this.context !== null) {
      keys(this.context).forEach(k => newParams['c.' + k] = this.context[k]);
    }
    if (this.filters !== undefined && this.filters !== null) {
      keys(this.filters).forEach(k => newParams['f.' + k] = this.filters[k]);
    }
    if (this.sorts !== undefined && this.sorts !== null) {
      keys(this.sorts).forEach(k => newParams['s.' + k] = this.sorts[k]);
    }
    if (this.display !== undefined && this.display !== null) {
      keys(this.display).forEach(k => newParams['d.' + k] = this.display[k]);
    }
    console.log('PlatformRouterLinkWithHref: new keys -> ' + JSON.stringify(keys(newParams)));
    return newParams;
  }

  ngOnChanges(changes: {}): any { this.updateTargetUrlAndHref(); }
  ngOnDestroy(): any { this.subscription.unsubscribe(); }

  @HostListener('click', ['$event.button', '$event.ctrlKey', '$event.metaKey', '$event.shiftKey'])
  onClick(button: number, ctrlKey: boolean, metaKey: boolean, shiftKey: boolean): boolean {
    if (button !== 0 || ctrlKey || metaKey || shiftKey) {
      return true;
    }

    if (typeof this.target === 'string' && this.target != '_self') {
      return true;
    }
    this.displayStateService.Route(this.commands);
    return false;
  }

  private updateTargetUrlAndHref(): void {
    this.href = this.locationStrategy.prepareExternalUrl(this.router.serializeUrl(this.urlTree));
  }

  get urlTree(): UrlTree {
    return this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: this.queryParams || this.commands,
      fragment: this.fragment,
      preserveFragment: attrBoolValue(this.preserveFragment),
    });
  }
  sendWarn() {
    UtilityFunctions.Log(
        1,
        'Invalid setting for [platformRouterLink]',
        this.clientSettingsService,
        this.appStateService);
  }
}

function attrBoolValue(s: any): boolean {
  return s === '' || !!s;
}